import { useContext } from 'react';

import { Box, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { Circle } from '@visx/shape';
import { DataContext } from '@visx/xychart';

import { type LineVariant, getLineVariantProps } from './series/LineSeries';

export type LegendItemConfig = {
  color?: string;
  marker?:
    | {
        shape: 'circle';
      }
    | {
        shape: 'line';
        variant?: LineVariant;
      };
  label?: string;
};

type LegendItem = {
  [dataKey: string]: LegendItemConfig;
};

type LegendProps = {
  config: LegendItem;
};

const LineMarker = ({ color, variant }: { color: string; variant?: LineVariant }) => {
  const variantProps = getLineVariantProps(variant);
  return (
    <svg width="26" viewBox="-1 0 26 12" className={css({ flexShrink: 0 })} aria-hidden="true" focusable="false">
      <line
        x1="0"
        y1="6"
        x2="24"
        y2="6"
        stroke={color}
        strokeLinecap="round"
        data-testid="legend-line"
        {...variantProps}
      />
    </svg>
  );
};

const CircleMarker = ({ color }: { color: string }) => {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" focusable="false">
      <Circle cx={4} cy={4} r={4} fill={color} data-testid="legend-circle" />
    </svg>
  );
};

export const Legend = ({ config }: LegendProps) => {
  const context = useContext(DataContext);
  const dataRegistryKeys = context.dataRegistry?.keys();
  const dataKeys = Object.keys(config ?? {}).filter(val =>
    !dataRegistryKeys ? true : dataRegistryKeys?.includes(val)
  );
  const colorScale = context?.colorScale;

  return (
    <Box
      css={{
        display: 'flex',
        gap: 'space.200',
        alignItems: 'center',
        flexWrap: 'wrap',
        pb: 'space.200',
        pt: 'space.100',
      }}
    >
      {dataKeys.map(dataKey => {
        const color = config?.[dataKey]?.color ?? (colorScale ? colorScale(dataKey) : '#000');
        return (
          <Box
            key={dataKey}
            css={{
              display: 'flex',
              gap: 'space.100',
              alignItems: 'center',
            }}
          >
            {config?.[dataKey]?.marker?.shape === 'line' ? (
              <LineMarker color={color} variant={config?.[dataKey]?.marker?.variant} />
            ) : (
              <CircleMarker color={color} />
            )}
            <Text variant="text50">{config?.[dataKey]?.label ?? dataKey}</Text>
          </Box>
        );
      })}
    </Box>
  );
};
